import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import logoIcon from '../assets/logo-icon-exact.png';
import allQuestions from './data/questions';
import Footer from '../components/Footer';

const QUIZ_VAULT_STORAGE_KEY = 'trp_quiz_vault_progress_v1';
const QUIZ_VAULT_PENDING_KEY = 'trp_pending_vault_quiz';
const QUIZ_VAULT_SIZE = 25;
const QUIZ_VAULT_TRACKS = 12;
const QUIZ_VAULT_PASS_PERCENT = 70;

const QUIZ_VAULT_LICENCES = [
  { value: 'master', label: 'Master Electrician' },
  { value: '309a', label: '309A Electrician' },
  { value: '442a', label: '442A Electrician' },
];

function getVaultProgressStorageId(userId) {
  return userId || 'guest';
}

function readVaultProgress(userId) {
  try {
    const raw = localStorage.getItem(QUIZ_VAULT_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed?.[getVaultProgressStorageId(userId)] || {};
  } catch {
    return {};
  }
}

function dedupeQuestions(pool) {
  const seen = new Set();
  return pool.filter(question => {
    if (seen.has(question.qid)) return false;
    seen.add(question.qid);
    return true;
  });
}

function buildQuizVaultQuestions(licence) {
  const required = QUIZ_VAULT_SIZE * QUIZ_VAULT_TRACKS;
  const primaryPool = dedupeQuestions(allQuestions.filter(q => q.licences.includes(licence)));
  if (licence !== 'master' || primaryPool.length >= required) {
    return primaryPool.slice(0, required);
  }

  const fallbackPool = dedupeQuestions([
    ...primaryPool,
    ...allQuestions.filter(q => q.licences.includes('309a') || q.licences.includes('442a')),
  ]);
  return fallbackPool.slice(0, required);
}

function getQuizVaultTracks(licence) {
  const pool = buildQuizVaultQuestions(licence);
  return Array.from({ length: QUIZ_VAULT_TRACKS }, (_, index) => {
    const start = index * QUIZ_VAULT_SIZE;
    return pool.slice(start, start + QUIZ_VAULT_SIZE);
  }).filter(track => track.length === QUIZ_VAULT_SIZE);
}

function getQuizVaultTrackProgress(progress, licence, trackNumber) {
  return progress?.[licence]?.[trackNumber] || null;
}

function isQuizVaultTrackUnlocked(progress, licence, trackNumber) {
  if (trackNumber === 1) return true;
  const previous = getQuizVaultTrackProgress(progress, licence, trackNumber - 1);
  return Number(previous?.bestPct || 0) >= QUIZ_VAULT_PASS_PERCENT;
}

function Vault({ session, isSubscribed }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryLicence = searchParams.get('licence');
  const defaultLicence = QUIZ_VAULT_LICENCES.some(option => option.value === queryLicence)
    ? queryLicence
    : 'master';

  const [vaultLicence, setVaultLicence] = useState(defaultLicence);
  const [vaultProgress, setVaultProgress] = useState(() => readVaultProgress(session?.user?.id || null));

  useEffect(() => {
    setVaultProgress(readVaultProgress(session?.user?.id || null));
  }, [session]);

  useEffect(() => {
    if (queryLicence && QUIZ_VAULT_LICENCES.some(option => option.value === queryLicence)) {
      setVaultLicence(queryLicence);
    }
  }, [queryLicence]);

  const tracks = useMemo(() => getQuizVaultTracks(vaultLicence), [vaultLicence]);
  const selectedLicenceLabel = useMemo(() => {
    return QUIZ_VAULT_LICENCES.find(option => option.value === vaultLicence)?.label || 'Master Electrician';
  }, [vaultLicence]);

  const openVaultQuiz = (trackNumber, unlocked) => {
    if (!isSubscribed || !unlocked) {
      navigate('/subscribe');
      return;
    }
    const pending = JSON.stringify({ licence: vaultLicence, trackNumber });
    localStorage.setItem(QUIZ_VAULT_PENDING_KEY, pending);
    navigate(`/quiz?vault=${encodeURIComponent(vaultLicence)}&vaultQuiz=${trackNumber}`, {
      replace: true,
      state: { autoVaultLicence: vaultLicence, autoVaultQuiz: trackNumber },
    });
  };

  return (
    <div className="vault-page">
      <header>
        <div className="logo" onClick={() => navigate(session ? '/quiz' : '/home')}>
          <div className="logo-icon"><img src={logoIcon} alt="" /></div>
          <div className="logo-text">TradeReady<span>Pro</span></div>
        </div>
        <div className="header-right">
          {!session && <Link to="/login" className="dark-toggle">Login</Link>}
          <Link to={session ? '/quiz' : '/home'} className="dark-toggle">Home</Link>
          {!session && <button className="btn-subscribe" onClick={() => navigate('/subscribe')}>Subscribe</button>}
          <Link to="/settings" className="dark-toggle" title="Settings">⚙️</Link>
        </div>
      </header>

      <main className="vault-wrap">
        <div className="quiz-vault-card vault-card-standalone">
          <div className="quiz-vault-header">
            <div>
              <div className="quiz-vault-eyebrow">Subscriber feature</div>
              <div className="quiz-vault-title-row">
                <h1 className="quiz-vault-title">Quiz Vault</h1>
                <span className="vault-title-separator" aria-hidden="true">-</span>
                <span className="vault-selected-licence">{selectedLicenceLabel}</span>
              </div>
              <p className="quiz-vault-copy">
                Fixed 25-question quizzes for your selected licence. Score {QUIZ_VAULT_PASS_PERCENT}% or more to unlock the next quiz.
              </p>
            </div>
          </div>

          <div className="quiz-vault-grid">
            {tracks.map((_, index) => {
              const trackNumber = index + 1;
              const unlocked = isQuizVaultTrackUnlocked(vaultProgress, vaultLicence, trackNumber);
              const progress = getQuizVaultTrackProgress(vaultProgress, vaultLicence, trackNumber);
              const passed = Number(progress?.bestPct || 0) >= QUIZ_VAULT_PASS_PERCENT;
              const visibleUnlocked = unlocked;
              const visiblePassed = passed;

              return (
                <button
                  key={`${vaultLicence}-${trackNumber}`}
                  type="button"
                  className={`quiz-vault-tile${visibleUnlocked ? '' : ' locked'}${visiblePassed ? ' passed' : ''}`}
                  onClick={() => openVaultQuiz(trackNumber, unlocked)}
                  disabled={isSubscribed ? !unlocked : false}
                >
                  <span className="quiz-vault-tile-top">
                    <span className="quiz-vault-tile-label">Quiz {trackNumber}</span>
                    <span className="quiz-vault-status-wrap">
                      {visibleUnlocked
                        ? <span className="quiz-vault-rocket-graphic" aria-hidden="true">🚀</span>
                        : <span className="quiz-vault-lock-graphic" aria-hidden="true" />}
                      <span className={`quiz-vault-status${visiblePassed ? ' passed' : visibleUnlocked ? ' unlocked' : ' locked'}`}>
                        {visiblePassed ? 'Passed' : visibleUnlocked ? 'Unlocked' : 'Locked'}
                      </span>
                    </span>
                  </span>
                  <span className="quiz-vault-score-line">25 fixed questions</span>
                  <span className="quiz-vault-score-line">
                    {!isSubscribed
                      ? 'Subscriber-only progression track'
                      : progress
                        ? (passed
                          ? (trackNumber === QUIZ_VAULT_TRACKS
                              ? 'Final quiz passed — course complete'
                              : `Passed — Quiz ${trackNumber + 1} is now unlocked`)
                          : `Best score: ${progress.bestScore}/${QUIZ_VAULT_SIZE} (${progress.bestPct}%)`)
                        : unlocked
                          ? (trackNumber === QUIZ_VAULT_TRACKS
                              ? 'Final vault quiz unlocked'
                              : `Unlocked — Quiz ${trackNumber + 1} is ready next`)
                          : `Score ${QUIZ_VAULT_PASS_PERCENT}% on Quiz ${trackNumber - 1} to unlock this quiz`}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}

export default Vault;
